import React, { useState, useEffect } from 'react';
import { Container, Button, Row, Col, Accordion } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './KarsilamaSayfasi.css';

// YouTube/watch veya youtu.be linklerini gömme (embed) adresine çevirir.
const gomulebilirVideo = (url) => {
  if (!url) return '';
  const eslesme = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (eslesme) return `https://www.youtube.com/embed/${eslesme[1]}`;
  return url;
};

function KarsilamaSayfasi() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Fiyat bölümü: 'aylik' | 'yillik'
  const [faturaDonemi, setFaturaDonemi] = useState('aylik');

  // Admin panelinden yönetilen içerik: ekran görüntüleri, video, yorumlar, istatistikler.
  const [icerik, setIcerik] = useState({ video_url: '', ekran_goruntuleri: [], yorumlar: [], istatistikler: [] });

  // Sunucu ısındırma: ziyaretçi landing'i okurken Render backend'i uyansın,
  // "Giriş Yap" veya "Demo Talep Et" dediğinde bekleme yaşamasın.
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/health`).catch(() => {});
    // Karşılama içeriği (public endpoint) — global yükleme göstergesini tetiklememek
    // için ham axios ile çekilir; hata olursa bölümler gizli kalır.
    axios.get(`${process.env.REACT_APP_API_URL}/landing-icerik`)
      .then((res) => setIcerik((onceki) => ({ ...onceki, ...res.data })))
      .catch(() => {});
  }, []);

  // Trafik ölçümü — giriş yapmış kullanıcıları (ör. admin) SAYMA.
  useEffect(() => {
    const girisli = window._accessToken || localStorage.getItem('accessToken');
    if (girisli) return;

    const API = process.env.REACT_APP_API_URL;

    // Görüntülenme: bu tarayıcı ilk kez geliyorsa tekil ziyaretçi olarak işaretlenir.
    let tekil = false;
    try {
      if (!localStorage.getItem('dt_ziyaretci')) {
        localStorage.setItem('dt_ziyaretci', '1');
        tekil = true;
      }
    } catch (e) { /* localStorage kapalı olabilir */ }
    axios.post(`${API}/ziyaret`, { tur: 'goruntuleme', tekil }).catch(() => {});

    // İnceleme: ziyaretçi sayfayı gerçekten incelediğinde (aşağı kaydırınca) bir kez.
    let incelemeGonderildi = false;
    const scrollHandler = () => {
      if (incelemeGonderildi) return;
      if (window.scrollY > window.innerHeight * 0.6) {
        incelemeGonderildi = true;
        window.removeEventListener('scroll', scrollHandler);
        axios.post(`${API}/ziyaret`, { tur: 'inceleme' }).catch(() => {});
      }
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', scrollHandler);
  }, []);

  const features = [
    { icon: 'group', title: 'Danışan Yönetimi', desc: 'Danışan bilgileri, ölçüm geçmişi ve beslenme planları tek ekranda. Kim, ne zaman, ne yapıyor — hepsi elinizin altında.' },
    { icon: 'restaurant_menu', title: 'Beslenme Planı & Şablonlar', desc: 'Kendi besin kütüphanenizle şablonlar oluşturun; haftalık diyet listelerini kopyala-yapıştır kolaylığında dakikalar içinde hazırlayın.' },
    { icon: 'calendar_month', title: 'Randevu Takvimi', desc: 'Çakışmaları önleyen akıllı takvim, müsaitlik kontrolü ve seans ücreti takibi tek yerde toplanır.' },
    { icon: 'monitoring', title: 'Gelişim Takibi', desc: 'Kilo, VKİ, bel/kalça/göğüs çevresi değişimini grafiklerle izleyin; danışanınızın ilerlemesini somut olarak gösterin.' },
    { icon: 'print', title: 'Logolu Çıktı', desc: 'Beslenme planlarını kendi logonuzla, tek tıkla yazdırmaya hazır PDF olarak alın. Markanız her danışanın elinde.' },
    { icon: 'inventory_2', title: 'Paket & Seans Yönetimi', desc: 'Danışana paket atayın, kalan seansı otomatik takip edin, süre dolarken uyarı alın; gelir takibinizi kaçırmayın.' },
    { icon: 'menu_book', title: 'Besin Kütüphanesi', desc: 'Kalori, protein, karbonhidrat ve yağ değerleriyle kendi besin ve ölçü birimi kütüphanenizi oluşturup tekrar tekrar kullanın.' },
    { icon: 'domain', title: 'Klinik Yönetimi', desc: 'Birden fazla diyetisyeni tek çatı altında yönetin, danışanları diyetisyenlere atayın, kliniğinizi tek panelden yönetin.' },
  ];

  const spotlights = [
    {
      icon: 'bolt',
      title: 'Şablonlarla saniyeler içinde plan',
      desc: 'Her danışana sıfırdan liste yazmayın. Bir kez oluşturduğunuz öğün şablonlarını ve besin kütüphanenizi kullanarak haftalık planları dakikalar içinde çıkarın.',
      bullets: ['Kendi besin kütüphaneniz ve ölçü birimleriniz', 'Hazır öğün şablonları', 'Kopyala-yapıştır ile haftalık liste', 'Otomatik kalori toplamı'],
    },
    {
      icon: 'workspace_premium',
      title: 'Kendi markanızla çıktılar',
      desc: 'Hazırladığınız planları kendi logonuzla, yazdırmaya hazır PDF olarak alın. Danışanınızın eline giden her belge sizin markanızı taşır.',
      bullets: ['Logolu, yazdırmaya hazır PDF', 'Tek tıkla çıktı', 'Randevuya bağlı belge arşivi', 'Tutarlı, düzenli sunum'],
    },
    {
      icon: 'insights',
      title: 'Ölçülebilir gelişim takibi',
      desc: 'Danışanınızın kilo, VKİ ve çevre ölçümlerindeki değişimi grafiklerle izleyin; ilerlemeyi görselleştirip motivasyonu yüksek tutun.',
      bullets: ['Kilo ve VKİ grafikleri', 'Bel / kalça / göğüs çevresi ölçümleri', 'Randevu bazlı geçmiş', 'Görsel, motive eden ilerleme'],
    },
  ];

  const steps = [
    { num: '01', title: 'Hesabınızı Oluşturun', desc: 'Kısa formu doldurun, kart bilgisi olmadan sistemi anında denemeye başlayın.' },
    { num: '02', title: 'Danışanlarınızı Ekleyin', desc: 'Danışan bilgilerini girin, paket atayın ve seans takibine başlayın.' },
    { num: '03', title: 'Planlamaya Başlayın', desc: 'Beslenme planları oluşturun, randevuları yönetin ve gelişimi takip edin.' },
  ];

  // Fiyat paketleri — tüm tutarlar KDV dahildir.
  // Yıllık planda 12 ay yerine 10 ay ödenir; kartta aylık karşılığı gösterilir.
  const paketler = [
    {
      ad: 'Demo',
      kimIcin: 'Önce deneyin, sonra karar verin',
      fiyat: { aylik: '0', yillik: '0' },
      birim: 'TL / 10 gün',
      dipnot: { aylik: 'Kart bilgisi gerekmez', yillik: 'Kart bilgisi gerekmez' },
      vurgulu: false,
      butonMetni: 'Ücretsiz Başla',
      butonVurgulu: false,
      ozellikler: [
        '5 danışana kadar',
        'Randevu ve öğün modülleri açık',
        'Ölçüm takibi ve grafikler',
        '10 gün sonunda verileriniz saklanır',
      ],
    },
    {
      ad: 'Başlangıç',
      kimIcin: 'Kendi ofisinde çalışan diyetisyenler',
      fiyat: { aylik: '899', yillik: '749' },
      birim: 'TL / ay',
      dipnot: {
        aylik: 'Aylık ödeme, istediğiniz zaman iptal',
        yillik: 'Yıllık 8.988 TL peşin — 2 ay bedava',
      },
      vurgulu: true,
      butonMetni: 'Ücretsiz Başla',
      butonVurgulu: true,
      ozellikler: [
        'Sınırsız danışan',
        'Randevu takvimi ve seans takibi',
        'Öğün şablonları ve besin kütüphanesi',
        'Kendi logonuzla yazdırmaya hazır PDF',
        'Kilo, VKİ ve çevre ölçümü grafikleri',
        'Paket ve seans yönetimi',
        'KVKK uyumlu altyapı, düzenli yedekleme',
      ],
    },
    {
      ad: 'Klinik',
      kimIcin: 'Birden fazla diyetisyenli merkezler',
      fiyat: { aylik: '719', yillik: '599' },
      birim: 'TL / diyetisyen / ay',
      dipnot: {
        aylik: 'En az 2 diyetisyen',
        yillik: 'Diyetisyen başına yıllık 7.188 TL',
      },
      vurgulu: false,
      butonMetni: 'Klinik Demosu Talep Et',
      butonVurgulu: false,
      ozellikler: [
        'Başlangıç paketindeki her şey',
        'Klinik yönetim paneli',
        'Danışanları diyetisyenlere atama',
        'Ortak klinik takvimi',
        'Diyetisyen bazlı seans ve gelir görünümü',
        'Kurulum ve veri taşıma desteği',
      ],
    },
  ];

  const faqs = [
    { q: 'Diyet-Takip nedir?', a: 'Diyet-Takip, diyetisyenler ve beslenme uzmanları için geliştirilmiş bulut tabanlı bir SaaS platformudur. Danışan yönetimi, beslenme planı oluşturma, randevu takibi ve gelişim analizi gibi tüm iş süreçlerinizi tek panelde toplar.' },
    { q: 'Ücretsiz deneyebilir miyim?', a: 'Evet. "Ücretsiz Başla" diyerek 10 günlük demo hesabınızı oluşturabilirsiniz. Kart bilgisi gerekmez; demo hesapta temel özellikleri kısıtlı limitlerle test edebilirsiniz.' },
    { q: 'Kliniğim için birden fazla diyetisyen kullanabilir mi?', a: 'Evet. Klinik yönetimi ile birden fazla diyetisyeni tek çatı altında toplayabilir, danışanları diyetisyenlere atayabilir ve tüm kliniği tek panelden yönetebilirsiniz.' },
    { q: 'Verilerim güvende mi?', a: 'Tüm verileriniz SSL şifreleme ile korunur, KVKK kapsamında işlenir, endüstri standardı güvenlik protokolleri uygulanır ve düzenli yedeklemeler yapılır.' },
    { q: 'Mobil cihazlardan erişebilir miyim?', a: 'Evet, Diyet-Takip tamamen responsive tasarıma sahiptir. Tablet ve mobil cihazlarınızdan tarayıcı üzerinden sorunsuz erişim sağlayabilirsiniz.' },
    { q: 'Kaç danışan ekleyebilirim?', a: 'Ücretli paketlerde danışan sınırı yoktur; 10 danışanla da 300 danışanla da aynı ücreti ödersiniz. Yalnızca 10 günlük demo hesaplarda 5 danışanlık bir sınır bulunur.' },
    { q: 'Başlangıç ve Klinik paketleri arasındaki fark ne?', a: 'Özellik farkı yoktur; ikisinde de randevu ve öğün modülleri, logolu PDF çıktısı ve ölçüm grafikleri açıktır. Klinik paketi buna birden fazla diyetisyeni tek çatı altında yönetme, danışanları diyetisyenlere atama ve ortak takvim özelliklerini ekler. Diyetisyen başına ücretlendirilir ve en az 2 diyetisyen gerektirir.' },
    { q: 'Yıllık ödemede ne kazanıyorum?', a: 'Yıllık planda 12 ay yerine 10 ay ödersiniz — yani iki ay bedava. Başlangıç paketi yıllık 8.988 TL, Klinik paketi ise diyetisyen başına yıllık 7.188 TL\'dir.' },
    { q: 'Ödemeyi nasıl yapıyorum?', a: 'Şu an ödemeler banka havalesi veya EFT ile alınmaktadır; aboneliğiniz ödeme onayının ardından aynı gün tanımlanır. Kredi kartıyla otomatik ödeme yakında eklenecektir.' },
    { q: 'Öğrenci veya yeni mezun indirimi var mı?', a: 'Evet. Yeni mezun ve öğrenci diyetisyenler için ilk 12 ay %50 indirim uygulanır. Diploma ya da öğrenci belgenizi göndermeniz yeterlidir.' },
  ];

  const gorseller = (icerik.ekran_goruntuleri || []).filter((g) => g && g.gorsel);
  const videoSrc = gomulebilirVideo(icerik.video_url);
  const yorumlar = icerik.yorumlar || [];
  const istatistikler = icerik.istatistikler || [];

  return (
    <div className="landing-container">
      <div className="aurora-background" />

      {/* ÜST NAVİGASYON */}
      <nav className="landing-navbar">
        <Container className="d-flex justify-content-between align-items-center">
          <div className="navbar-brand-custom d-flex align-items-center gap-2">
            <div className="brand-icon">
              <span className="material-symbols-outlined">clinical_notes</span>
            </div>
            <span className="brand-text">Diyet-Takip</span>
          </div>

          {/* Desktop Nav */}
          <div className="nav-links-desktop d-none d-md-flex align-items-center gap-4">
            <a href="#features" className="nav-link-custom">Özellikler</a>
            <a href="#nasil-calisir" className="nav-link-custom">Nasıl Çalışır?</a>
            <a href="#fiyatlar" className="nav-link-custom">Fiyatlar</a>
            <a href="#sss" className="nav-link-custom">SSS</a>
            <Button variant="outline-light" size="sm" className="demo-btn-nav" onClick={() => navigate('/demo-talep')}>
              Ücretsiz Başla
            </Button>
            <Button variant="primary" size="sm" className="login-btn-nav" onClick={() => navigate('/giris')}>
              Giriş Yap
            </Button>
          </div>

          {/* Mobile Hamburger */}
          <button className="hamburger-btn d-md-none" onClick={() => setMenuOpen(!menuOpen)}>
            <span className="material-symbols-outlined">{menuOpen ? 'close' : 'menu'}</span>
          </button>
        </Container>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="mobile-menu d-md-none">
            <a href="#features" className="mobile-link" onClick={() => setMenuOpen(false)}>Özellikler</a>
            <a href="#nasil-calisir" className="mobile-link" onClick={() => setMenuOpen(false)}>Nasıl Çalışır?</a>
            <a href="#fiyatlar" className="mobile-link" onClick={() => setMenuOpen(false)}>Fiyatlar</a>
            <a href="#sss" className="mobile-link" onClick={() => setMenuOpen(false)}>SSS</a>
            <Button variant="outline-light" size="sm" className="w-100 mt-2" onClick={() => { setMenuOpen(false); navigate('/demo-talep'); }}>Ücretsiz Başla</Button>
            <Button variant="primary" size="sm" className="w-100 mt-2" onClick={() => { setMenuOpen(false); navigate('/giris'); }}>Giriş Yap</Button>
          </div>
        )}
      </nav>

      <Container className="landing-content">
        {/* HERO BÖLÜMÜ */}
        <div className="hero-section text-center">
          <div className="hero-badge mb-3">
            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">
              ✨ Diyetisyenler için bulut tabanlı yönetim
            </span>
          </div>
          <h1 className="hero-title display-3 fw-bolder">
            Danışan takibinin yükünü yazılıma bırakın, <span className="text-gradient">siz danışanınıza odaklanın</span>
          </h1>
          <p className="hero-description lead my-4 mx-auto">
            Randevudan beslenme planına, ölçüm takibinden paket yönetimine kadar tüm operasyonel işlerinizi tek panelde toplayın; planlarınızı dakikalar içinde, kendi logonuzla hazırlayın.
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4 flex-wrap">
            <Button variant="primary" size="lg" className="px-5 py-3 fw-bold hero-btn-primary" onClick={() => navigate('/demo-talep')}>
              Ücretsiz Başla <span>&rarr;</span>
            </Button>
            <Button variant="outline-secondary" size="lg" className="px-4 py-3 fw-bold hero-btn-secondary" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
              Keşfet
            </Button>
          </div>

          {/* GÜVEN ŞERİDİ */}
          <div className="trust-strip d-flex justify-content-center flex-wrap gap-3 gap-md-4 mt-4">
            {['Kart bilgisi gerekmez', '10 gün ücretsiz', 'Dakikalar içinde kurulum', 'KVKK uyumlu'].map((t, i) => (
              <span className="trust-item d-inline-flex align-items-center gap-1" key={i}>
                <span className="material-symbols-outlined">check_circle</span>{t}
              </span>
            ))}
          </div>
        </div>

        {/* İSTATİSTİKLER (admin panelinden gelir; boşsa gizli) */}
        {istatistikler.length > 0 && (
          <div className="stats-section">
            <Row className="g-3 justify-content-center text-center">
              {istatistikler.map((s, i) => (
                <Col xs={6} md={3} key={i}>
                  <div className="stat-card">
                    <div className="stat-value text-gradient">{s.deger}</div>
                    <div className="stat-label">{s.etiket}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* ÖZELLİKLER GRID */}
        <div id="features" className="features-section">
          <div className="text-center mb-5">
            <h2 className="section-title">İhtiyacınız Olan Her Şey, <span className="text-gradient">Tek Platformda</span></h2>
            <p className="section-subtitle">Modern diyetisyen pratiğinizi baştan sona destekleyen araçlar.</p>
          </div>
          <Row className="g-4">
            {features.map((f, i) => (
              <Col md={6} lg={4} key={i}>
                <div className="feature-card">
                  <div className="feature-card-icon">
                    <span className="material-symbols-outlined">{f.icon}</span>
                  </div>
                  <h4 className="feature-card-title">{f.title}</h4>
                  <p className="feature-card-desc">{f.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* SPOTLIGHT — EN GÜÇLÜ YETENEKLER */}
        <div className="spotlight-section">
          <div className="text-center mb-5">
            <h2 className="section-title">Zamanınızı Geri <span className="text-gradient">Kazandıran Detaylar</span></h2>
            <p className="section-subtitle">Diyet-Takip'i günlük pratiğinizde vazgeçilmez kılan üç yetenek.</p>
          </div>
          {spotlights.map((sp, i) => (
            <Row key={i} className={`spotlight-row align-items-center g-4 ${i % 2 === 1 ? 'flex-md-row-reverse' : ''}`}>
              <Col md={6}>
                <div className="spotlight-visual">
                  <span className="material-symbols-outlined spotlight-visual-icon">{sp.icon}</span>
                </div>
              </Col>
              <Col md={6}>
                <h3 className="spotlight-title">{sp.title}</h3>
                <p className="spotlight-desc">{sp.desc}</p>
                <ul className="spotlight-list">
                  {sp.bullets.map((b, j) => (
                    <li key={j}><span className="material-symbols-outlined">check</span>{b}</li>
                  ))}
                </ul>
              </Col>
            </Row>
          ))}
        </div>

        {/* ÜRÜNÜ GÖRÜN — ekran görüntüleri & video (admin panelinden; boşsa gizli) */}
        {(gorseller.length > 0 || videoSrc) && (
          <div id="urunu-gorun" className="showcase-section">
            <div className="text-center mb-5">
              <h2 className="section-title">Ürünü <span className="text-gradient">Yakından Görün</span></h2>
              <p className="section-subtitle">Diyet-Takip'in ekranlarını keşfedin.</p>
            </div>

            {videoSrc && (
              <div className="showcase-video mx-auto mb-5">
                <div className="video-ratio">
                  <iframe
                    src={videoSrc}
                    title="Diyet-Takip tanıtım videosu"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {gorseller.length > 0 && (
              <Row className="g-4">
                {gorseller.map((g, i) => (
                  <Col md={6} key={i}>
                    <div className="shot-card">
                      <img src={g.gorsel} alt={g.baslik || `Ekran görüntüsü ${i + 1}`} loading="lazy" />
                      {(g.baslik || g.aciklama) && (
                        <div className="shot-caption">
                          {g.baslik && <h5 className="shot-title">{g.baslik}</h5>}
                          {g.aciklama && <p className="shot-desc">{g.aciklama}</p>}
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        )}

        {/* NASIL ÇALIŞIR */}
        <div id="nasil-calisir" className="how-section">
          <div className="text-center mb-5">
            <h2 className="section-title">Başlamak <span className="text-gradient">Çok Kolay</span></h2>
            <p className="section-subtitle">Üç adımda dijital dönüşümünüzü tamamlayın.</p>
          </div>
          <Row className="g-4 justify-content-center">
            {steps.map((s, i) => (
              <Col md={4} key={i}>
                <div className="step-card">
                  <div className="step-number">{s.num}</div>
                  <h4 className="step-title">{s.title}</h4>
                  <p className="step-desc">{s.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* FİYATLANDIRMA */}
        <div id="fiyatlar" className="pricing-section">
          <div className="text-center mb-4">
            <h2 className="section-title">Tek Fiyat, <span className="text-gradient">Tüm Özellikler</span></h2>
            <p className="section-subtitle">
              Paketler arasında özellik kısıtlaması yok, danışan sınırı yok. Bir danışanınızın
              aylık ücretinden daha azına tüm pratiğiniz tek panelde.
            </p>
          </div>

          {/* Aylık / Yıllık seçici */}
          <div className="billing-toggle">
            <div className="billing-switch" role="group" aria-label="Faturalama dönemi">
              <button
                type="button"
                className="billing-option"
                aria-pressed={faturaDonemi === 'aylik'}
                onClick={() => setFaturaDonemi('aylik')}
              >
                Aylık
              </button>
              <button
                type="button"
                className="billing-option"
                aria-pressed={faturaDonemi === 'yillik'}
                onClick={() => setFaturaDonemi('yillik')}
              >
                Yıllık
              </button>
            </div>
            <span className="billing-save">2 ay bedava</span>
          </div>

          <Row className="g-4 align-items-stretch">
            {paketler.map((p, i) => (
              <Col md={6} lg={4} key={i}>
                <div className={`plan-card${p.vurgulu ? ' plan-card-featured' : ''}`}>
                  {p.vurgulu && <span className="plan-badge">En çok tercih edilen</span>}
                  <h4 className="plan-name">{p.ad}</h4>
                  <p className="plan-for">{p.kimIcin}</p>

                  <div className="plan-price-row">
                    <span className="plan-price">{p.fiyat[faturaDonemi]}</span>
                    <span className="plan-price-unit">{p.birim}</span>
                  </div>
                  <p className="plan-price-note">{p.dipnot[faturaDonemi]}</p>

                  <Button
                    variant={p.butonVurgulu ? 'primary' : 'outline-secondary'}
                    className={`plan-cta w-100 fw-bold ${p.butonVurgulu ? 'hero-btn-primary' : 'hero-btn-secondary'}`}
                    onClick={() => navigate('/demo-talep')}
                  >
                    {p.butonMetni}
                  </Button>

                  <ul className="plan-features">
                    {p.ozellikler.map((oz, j) => (
                      <li key={j}>
                        <span className="material-symbols-outlined">check</span>{oz}
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            ))}
          </Row>

          <p className="pricing-vat-note">Tüm fiyatlara KDV dahildir. Kurumsal fatura düzenlenir.</p>

          {/* Yeni mezun / öğrenci indirimi */}
          <div className="student-strip">
            <div className="student-strip-icon">
              <span className="material-symbols-outlined">school</span>
            </div>
            <div className="student-strip-text">
              <h5>Yeni mezun veya öğrenci misiniz?</h5>
              <p>İlk 12 ay %50 indirim. Diploma ya da öğrenci belgenizi göndermeniz yeterli.</p>
            </div>
            <div className="student-strip-amount">
              <strong>449 TL/ay</strong>
              <span>899 TL</span>
            </div>
          </div>
        </div>

        {/* DANIŞAN YORUMLARI (admin panelinden gelir; boşsa gizli) */}
        {yorumlar.length > 0 && (
          <div id="yorumlar" className="testimonials-section">
            <div className="text-center mb-5">
              <h2 className="section-title">Diyetisyenler <span className="text-gradient">Ne Diyor?</span></h2>
              <p className="section-subtitle">Diyet-Takip'i günlük pratiğinde kullananların deneyimleri.</p>
            </div>
            <Row className="g-4">
              {yorumlar.map((y, i) => (
                <Col md={6} lg={4} key={i}>
                  <div className="testimonial-card">
                    <span className="material-symbols-outlined testimonial-quote">format_quote</span>
                    <p className="testimonial-text">{y.metin}</p>
                    <div className="testimonial-author">
                      <div className="testimonial-avatar">{(y.isim || '?').trim().charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="testimonial-name">{y.isim}</div>
                        {y.unvan && <div className="testimonial-role">{y.unvan}</div>}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* SSS */}
        <div id="sss" className="faq-section">
          <div className="text-center mb-5">
            <h2 className="section-title">Sık Sorulan <span className="text-gradient">Sorular</span></h2>
            <p className="section-subtitle">Merak ettiklerinize hızlı yanıtlar.</p>
          </div>
          <div className="faq-wrapper mx-auto" style={{ maxWidth: '800px' }}>
            <Accordion flush>
              {faqs.map((faq, i) => (
                <Accordion.Item eventKey={String(i)} key={i} className="faq-item">
                  <Accordion.Header>{faq.q}</Accordion.Header>
                  <Accordion.Body>{faq.a}</Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>
        </div>

        {/* CTA BÖLÜMÜ */}
        <div className="cta-section text-center">
          <div className="cta-card">
            <h2 className="cta-title">Dijital Dönüşüme Hazır Mısınız?</h2>
            <p className="cta-desc">10 günlük ücretsiz demo ile sistemimizi hemen deneyin. Kart bilgisi gerekmez.</p>
            <Button variant="primary" size="lg" className="px-5 py-3 fw-bold hero-btn-primary" onClick={() => navigate('/demo-talep')}>
              Ücretsiz Başla <span>&rarr;</span>
            </Button>
          </div>
        </div>
      </Container>

      {/* FOOTER */}
      <footer className="landing-footer">
        <Container>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <div className="brand-icon-sm">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>clinical_notes</span>
              </div>
              <span className="footer-brand">Diyet-Takip</span>
            </div>
            <div className="footer-links">
              <a href="#features">Özellikler</a>
              <a href="#nasil-calisir">Nasıl Çalışır?</a>
              <a href="#fiyatlar">Fiyatlar</a>
              <a href="#sss">SSS</a>
              <a href="/kvkk">KVKK</a>
              <a href="/gizlilik-politikasi">Gizlilik</a>
            </div>
            <p className="footer-copy m-0">© {new Date().getFullYear()} Diyet-Takip. Tüm hakları saklıdır.</p>
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default KarsilamaSayfasi;
